import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../../data/content";
import { BuyButton } from "./ui";
import { trackCircleCta } from "../../lib/trackCircleCta";

// Canonical first-party entry point to the Self Doubling Growing Circle.
// No dependency on selfdoubling.app — the Circle now lives under the same
// origin as the website (/growing-circle/*).
const GROWING_CIRCLE_HREF = "/growing-circle";

const GrowingCircleButton = ({ mobile = false, onClick, testId, location }) => {
  const handleClick = (e) => {
    trackCircleCta(location || (mobile ? "header_mobile" : "header"));
    if (onClick) onClick(e);
  };
  // Deep burgundy / wine tone — preserved from the previous "Reader
  // Platform" button. The slightly longer wording is accommodated by
  // trimming the inline padding one notch on desktop; the mobile
  // variant remains full-width. The small ArrowUpRight communicates
  // "opens the members' area" visually.
  const base = mobile
    ? "w-full mt-2 text-center py-3 rounded-md font-label text-[0.72rem] tracking-[0.22em] uppercase"
    : "inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-label text-[0.62rem] tracking-[0.18em] uppercase whitespace-nowrap";
  return (
    <Link
      to={GROWING_CIRCLE_HREF}
      onClick={handleClick}
      data-testid={testId}
      className={`${base} transition-all duration-300 ring-1 ring-inset ring-[rgba(122,30,47,0.85)] text-[#f5ecdf] shadow-[0_2px_10px_-3px_rgba(122,30,47,0.55)] hover:ring-[rgba(154,42,63,1)] hover:shadow-[0_4px_18px_-4px_rgba(154,42,63,0.75)]`}
      style={{
        backgroundColor: "rgba(122,30,47,0.88)",
        backgroundImage:
          "linear-gradient(180deg, rgba(154,42,63,0.95) 0%, rgba(90,26,36,0.95) 100%)",
      }}
    >
      <span>Self Doubling Growing Circle</span>
      <ArrowUpRight
        size={mobile ? 13 : 11}
        aria-hidden
        strokeWidth={1.75}
        className="-mr-0.5 opacity-90"
      />
    </Link>
  );
};

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen ? "bg-[rgba(10,9,8,0.9)] backdrop-blur-md border-b border-[var(--gold-line)]" : "bg-transparent"
      }`}
      data-testid="site-header"
      data-section="header"
      data-section-label="Header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 xl:px-6 2xl:px-10 h-16 sm:h-20 flex items-center justify-between gap-6">
        <a href="#top" className="font-display text-cream text-sm sm:text-base tracking-[0.22em] flex-none" data-testid="logo">
          SELF DOUBLING<span className="text-gold">©</span>
        </a>

        <nav className="hidden xl:flex items-center gap-x-6 2xl:gap-x-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-label text-[0.68rem] tracking-[0.14em] 2xl:text-[0.7rem] 2xl:tracking-[0.16em] uppercase text-secondary hover:text-gold transition-colors"
              data-testid={`nav-${l.href.replace("#", "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3 xl:gap-4 2xl:gap-6 xl:pl-5 xl:ml-2 xl:border-l xl:border-[var(--gold-line)] flex-none">
          <BuyButton
            location="header"
            size="sm"
            testId="buy-btn-header"
            label={
              <>
                <span className="sm:hidden">Buy</span>
                <span className="hidden sm:inline" aria-hidden="true">Buy the Book</span>
              </>
            }
          />
          <div className="hidden md:block">
            <GrowingCircleButton testId="growing-circle-btn-header" />
          </div>
          <button
            className="xl:hidden text-cream p-2 -mr-2"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            data-testid="menu-toggle"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-[var(--gold-line)]"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="font-editorial text-xl text-secondary hover:text-gold transition-colors py-3 border-b border-[var(--gold-line)] last:border-0"
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </a>
              ))}
              <GrowingCircleButton
                mobile
                onClick={() => setMenuOpen(false)}
                testId="growing-circle-btn-mobile"
              />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
